import { useState, type ReactNode } from "react";
import type { ConditionsView, FieldType, InventoryResponse, ProfileField } from "@starsach/shared-types";
import {
  useBind,
  useBindings,
  useClearOverride,
  useConfigVersions,
  useCreateProfile,
  useInventory,
  useProfile,
  useProfiles,
  usePublishProfile,
  useRollback,
  useSetAlias,
  useUnbind,
  useSetOverride,
} from "../../api/hooks";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { FIELD_TYPES, FIELD_TYPE_LABELS, fieldProblem, newField } from "../../lib/profile-fields";
import { HoursEditor, MenuEditor } from "./ProfileFieldEditors";
import { PromotionPanel } from "./PromotionPanel";

/**
 * Flow configuration (requirements.md section 10): one contact flow
 * serving many numbers, driven by a profile authored here rather than by
 * editing the flow.
 */
export function FlowConfigTab({ environmentId, enabled }: { environmentId: string; enabled: boolean }) {
  const profiles = useProfiles(environmentId);
  const [selected, setSelected] = useState<string>();
  const [creating, setCreating] = useState(false);
  const profileId = selected ?? profiles.data?.profiles[0]?.profileId;
  const detail = useProfile(environmentId, profileId);
  const created = (id: string) => {
    setSelected(id);
    setCreating(false);
  };

  if (!enabled) {
    return (
      <EmptyState
        title="Flow configuration is not deployed in this account"
        hint="Redeploy the customer template with EnableFlowConfiguration set to true. It needs a Connect instance id."
      />
    );
  }
  if (profiles.isPending) return <LoadingState label="Loading profiles" />;
  if (profiles.isError) return <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />;
  if (profiles.data.profiles.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState title="No profiles yet" hint="A profile is the configuration for one line your callers dial - its opening hours, holidays, announcements, menus and where calls go - so the contact flow never has to change. 'Global Helpdesk' or 'Sales' would each be one. It is written once and works in every environment; a binding decides which numbers reach it. Start with a name; the rest is added afterwards." />
        <NewProfileForm environmentId={environmentId} onCreated={created} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <nav aria-label="Profiles" className="flex flex-wrap items-center gap-2">
        {profiles.data.profiles.map((p) => (
          <button
            key={p.profileId}
            type="button"
            aria-current={p.profileId === profileId ? "true" : undefined}
            onClick={() => setSelected(p.profileId)}
            className={`rounded border px-3 py-1.5 text-sm ${p.profileId === profileId ? "border-accent text-accent-strong dark:text-white" : "border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-300"}`}
          >
            {p.name}
            {p.configVersion ? <span className="ml-2 text-xs text-neutral-500">v{p.configVersion}</span> : <span className="ml-2 text-xs text-neutral-500">not published here</span>}
          </button>
        ))}
        <button type="button" onClick={() => setCreating((c) => !c)} aria-expanded={creating} className={`${btnSecondary} min-h-8 px-3 text-xs`}>
          New profile
        </button>
      </nav>
      {creating && <NewProfileForm environmentId={environmentId} onCreated={created} onCancel={() => setCreating(false)} />}

      {detail.isPending && <LoadingState label="Loading configuration" />}
      {detail.isError && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
      {detail.data && profileId && (
        <>
          <Conditions conditions={detail.data.conditions} environmentId={environmentId} />
          <FieldsCard
            environmentId={environmentId}
            profileId={profileId}
            fields={detail.data.fields}
            fieldMeta={detail.data.fieldMeta}
            requiresApproval={detail.data.requiresApproval}
            overrides={detail.data.overrides}
          />
          <NumbersCard environmentId={environmentId} profileId={profileId} profiles={profiles.data.profiles} />
          <PromotionPanel environmentId={environmentId} profileId={profileId} chain={detail.data.chain} />
          <Versions environmentId={environmentId} profileId={profileId} />
        </>
      )}
    </div>
  );
}

/**
 * Section 10.2: a binding is what makes a profile reachable - dialled
 * number -> profile - and sync serves only bound profiles. The numbers are
 * the instance's own, from the inventory; nothing is typed.
 */
function NumbersCard({ environmentId, profileId, profiles }: { environmentId: string; profileId: string; profiles: Array<{ profileId: string; name: string }> }) {
  const inventory = useInventory(environmentId);
  const bindings = useBindings(environmentId);
  const bind = useBind(environmentId);
  const unbind = useUnbind(environmentId);
  const selected = profiles.find((p) => p.profileId === profileId);
  const numbers = (inventory.data?.items ?? []).filter((i) => i.type === "phone_number").map((i) => i.name);
  const bound = new Map((bindings.data?.bindings ?? []).map((b) => [b.phoneNumber, b]));
  // A number bound by hand to a profile the inventory no longer lists still needs to be visible to unbind.
  const rows = [...new Set([...numbers, ...bound.keys()])].map((phoneNumber) => ({ phoneNumber, binding: bound.get(phoneNumber) }));
  const busy = bind.isPending || unbind.isPending;

  return (
    <Card title="Numbers">
      {(inventory.isPending || bindings.isPending) && <LoadingState label="Loading numbers" />}
      {bindings.isError && <ErrorState error={bindings.error} onRetry={() => void bindings.refetch()} />}
      {inventory.data && bindings.data && (
        <DataTable
          caption="Which profile each number in this instance reaches"
          columns={[
            { key: "number", header: "Number", render: (r) => <span className="font-medium">{r.phoneNumber}</span> },
            {
              key: "reaches",
              header: "Reaches",
              render: (r) =>
                r.binding ? (
                  <>
                    {profiles.find((p) => p.profileId === r.binding!.profileId)?.name ?? r.binding.profileId}
                    <span className="ml-2 text-xs text-neutral-500">
                      since <RelativeTime iso={r.binding.boundAt} /> by {r.binding.boundBy}
                    </span>
                  </>
                ) : (
                  <StatusPill status="not bound" tone="warning" />
                ),
            },
            {
              key: "action",
              header: "",
              render: (r) =>
                r.binding?.profileId === profileId ? (
                  <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => unbind.mutate({ phoneNumber: r.phoneNumber })}>
                    Unbind
                  </button>
                ) : (
                  <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => bind.mutate({ phoneNumber: r.phoneNumber, profileId })}>
                    {r.binding ? `Move to ${selected?.name ?? "this profile"}` : `Bind to ${selected?.name ?? "this profile"}`}
                  </button>
                ),
            },
          ]}
          rows={rows}
          rowKey={(r) => r.phoneNumber}
          sortBy={(r) => r.phoneNumber}
          filter={rows.length > 3}
          empty={<EmptyState title="No numbers yet" hint="The instance's phone numbers arrive with its inventory, published by the stack every six hours. Claim a number in Amazon Connect and it appears here on the next inventory." />}
        />
      )}
      {(bind.isError || unbind.isError) && <ErrorState error={bind.error ?? unbind.error} />}
    </Card>
  );
}

/**
 * Section 10.6 keeps four conditions apart, and so does this: a version
 * gap and a stale override are operational facts, while sync lag and
 * inventory drift mean something is broken. Conflating them trains
 * people to ignore the two that matter.
 */
function Conditions({ conditions, environmentId }: { conditions: ConditionsView; environmentId: string }) {
  const drift = conditions.inventoryDrift ?? [];
  const faults = [
    conditions.syncLag && {
      key: "sync",
      text: (
        <>
          This environment is serving {conditions.syncLag.syncedVersion === undefined ? "no version" : `v${conditions.syncLag.syncedVersion}`} but v{conditions.syncLag.publishedVersion} was
          published <RelativeTime iso={conditions.syncLag.publishedAt} />.{" "}
          {conditions.syncLag.syncedAt ? (
            <>It last pulled its configuration <RelativeTime iso={conditions.syncLag.syncedAt} />.</>
          ) : (
            "It has never pulled its configuration - check that the flow configuration component is deployed there."
          )}
        </>
      ),
    },
    drift.length > 0 && {
      key: "drift",
      text: `${drift.length} reference no longer resolves in this instance. Nothing authored changed; the environment moved underneath it.`,
    },
  ].filter(Boolean) as Array<{ key: string; text: ReactNode }>;

  const facts = [
    conditions.unbound && {
      key: "unbound",
      text: "No number reaches this profile in this environment yet, so nothing is served from it. Bind one under Numbers below.",
    },
    conditions.pendingSync && {
      key: "pending",
      text: (
        <>
          v{conditions.pendingSync.publishedVersion} was published <RelativeTime iso={conditions.pendingSync.publishedAt} /> and this environment has not pulled it yet; it checks every few minutes.
        </>
      ),
    },
    conditions.versionGap && {
      key: "gap",
      text: `${conditions.versionGap.behind} version${conditions.versionGap.behind === 1 ? "" : "s"} behind ${conditions.versionGap.sourceEnvironmentId}.`,
    },
    ...conditions.staleOverrides.map((o) => ({
      key: `override-${o.field}`,
      text: `Override on ${o.field} has been active ${o.ageDays} days${o.expiresAt ? " and has expired" : " with no expiry"}, set by ${o.setBy}.`,
    })),
  ].filter(Boolean) as Array<{ key: string; text: ReactNode }>;

  if (faults.length === 0 && facts.length === 0) return null;
  return (
    <div className="space-y-2">
      {faults.length > 0 && (
        <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">Needs attention</p>
          <ul className="mt-1 list-disc pl-5">{faults.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
          {drift.map((d) => <AliasFixer key={d.field} environmentId={environmentId} drift={d} />)}
        </div>
      )}
      {facts.length > 0 && (
        <div role="status" className="rounded border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          <ul className="list-disc pl-5">{facts.map((f) => <li key={f.key}>{f.text}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function FieldsCard({
  environmentId,
  profileId,
  fields,
  fieldMeta,
  requiresApproval,
  overrides,
}: {
  environmentId: string;
  profileId: string;
  fields: ProfileField[];
  fieldMeta: Record<string, { locally_modified: boolean; last_changed_at: string; last_changed_by: string }>;
  requiresApproval: boolean;
  overrides: Array<{ field: string; value: unknown; expiresAt?: string; setBy: string; reason: string }>;
}) {
  const inventory = useInventory(environmentId);
  const publish = usePublishProfile(environmentId, profileId);
  const [draft, setDraft] = useState<ProfileField[]>(fields);
  const [dirty, setDirty] = useState(false);

  const update = (index: number, next: ProfileField) => {
    setDraft(draft.map((f, i) => (i === index ? next : f)));
    setDirty(true);
  };
  const remove = (index: number) => {
    setDraft(draft.filter((_, i) => i !== index));
    setDirty(true);
  };

  return (
    <Card title="Configuration">
      {draft.length === 0 && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Nothing is configured yet. Add the first field below - an announcement or the opening hours are the usual start - then publish.
        </p>
      )}
      <ul className="space-y-3">
        {draft.map((field, index) => {
          const key = `${field.type}/${field.name}`;
          const override = overrides.find((o) => o.field === key);
          const meta = fieldMeta[key];
          return (
            <li key={key}>
              <div className="flex flex-wrap items-baseline gap-2">
                <label htmlFor={`field-${key}`} className="text-sm font-medium">{field.name}</label>
                <span className="text-xs text-neutral-500">{FIELD_TYPE_LABELS[field.type]}</span>
                {meta?.locally_modified && <StatusPill status="changed here" tone="warning" />}
                {override && <StatusPill status="overridden" tone="critical" />}
                <button type="button" className="ml-auto text-xs text-neutral-500 underline hover:text-ink dark:hover:text-white" onClick={() => remove(index)} aria-label={`Remove ${field.name}`}>
                  Remove field
                </button>
              </div>
              {"ref" in field ? (
                <select
                  id={`field-${key}`}
                  className="mt-1 w-full rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                  value={field.ref}
                  onChange={(e) => update(index, { ...field, ref: e.target.value })}
                >
                  {/* Real names from this instance: the customer never sees or types an ARN. */}
                  {(inventory.data?.items ?? []).filter((i) => i.type === field.type).map((i) => (
                    <option key={i.name} value={i.name}>{i.name}</option>
                  ))}
                  {!(inventory.data?.items ?? []).some((i) => i.type === field.type && i.name === field.ref) && (
                    <option value={field.ref}>{field.ref} (not in this instance)</option>
                  )}
                </select>
              ) : field.type === "announcement" || field.type === "kv" || field.type === "voice" ? (
                <input
                  id={`field-${key}`}
                  className="mt-1 w-full rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                  value={field.value}
                  onChange={(e) => update(index, { ...field, value: e.target.value } as ProfileField)}
                />
              ) : field.type === "menu" ? (
                <MenuEditor field={field} inventory={inventory.data} onChange={(value) => update(index, { ...field, value })} />
              ) : (
                <HoursEditor field={field} onChange={(value) => update(index, { ...field, value })} />
              )}
              {override && (
                <OverrideRow environmentId={environmentId} profileId={profileId} field={key} override={override} />
              )}
            </li>
          );
        })}
      </ul>

      <AddField
        existing={draft}
        inventory={inventory.data}
        onAdd={(field) => {
          setDraft([...draft, field]);
          setDirty(true);
        }}
      />

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          className={btnPrimary}
          disabled={!dirty || publish.isPending}
          onClick={() => publish.mutate({ fields: draft })}
        >
          {requiresApproval ? "Send for approval" : "Publish"}
        </button>
        <NewOverride environmentId={environmentId} profileId={profileId} fields={draft} />
      </div>
      {publish.isSuccess && (
        <p role="status" className="mt-3 text-sm text-green-800 dark:text-green-300">
          {publish.data.status === "published" ? `Published as v${publish.data.configVersion}.` : "Sent for a manager to approve."}
        </p>
      )}
      {publish.isError && <ErrorState error={publish.error} />}
    </Card>
  );
}

/** Section 10.2: everything is a (type, name); the name is what the contact flow reads it by. */
function AddField({ existing, inventory, onAdd }: { existing: ProfileField[]; inventory: InventoryResponse | undefined; onAdd: (field: ProfileField) => void }) {
  const [type, setType] = useState<FieldType>("announcement");
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string>();

  const add = () => {
    const why = fieldProblem(type, name, existing, inventory);
    setProblem(why);
    if (why) return;
    onAdd(newField(type, name, inventory));
    setName("");
  };

  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
      aria-label="Add a field"
      onSubmit={(e) => {
        e.preventDefault();
        add();
      }}
    >
      <label className="text-xs">
        Type
        <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as FieldType)}>
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
          ))}
        </select>
      </label>
      <label className="text-xs">
        Name
        <input
          className={inputClass}
          value={name}
          placeholder="welcome"
          aria-describedby={problem ? "add-field-problem" : undefined}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => {
            setName(e.target.value);
            setProblem(undefined);
          }}
        />
      </label>
      <button type="submit" className={btnSecondary}>Add field</button>
      {problem && <p id="add-field-problem" role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
    </form>
  );
}

function OverrideRow({
  environmentId,
  profileId,
  field,
  override,
}: {
  environmentId: string;
  profileId: string;
  field: string;
  override: { value: unknown; expiresAt?: string; setBy: string; reason: string };
}) {
  const clear = useClearOverride(environmentId);
  return (
    <p className="mt-1 rounded bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
      Playing <q>{String(override.value)}</q> instead - {override.reason}, set by {override.setBy}
      {override.expiresAt ? <> until <RelativeTime iso={override.expiresAt} /></> : ", with no expiry"}.
      <button type="button" className="ml-2 underline" disabled={clear.isPending} onClick={() => clear.mutate({ profileId, field })}>
        End override
      </button>
    </p>
  );
}

/**
 * Setting one is fast and low-ceremony, and never gated: it happens
 * during an incident, by someone who is not a developer. The form makes
 * the distinction unavoidable - until further notice, or until when?
 */
function NewOverride({ environmentId, profileId, fields }: { environmentId: string; profileId: string; fields: ProfileField[] }) {
  const set = useSetOverride(environmentId);
  const [open, setOpen] = useState(false);
  const [field, setField] = useState(`${fields[0]?.type}/${fields[0]?.name}`);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  if (!open) {
    return <button type="button" className="rounded border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700" onClick={() => setOpen(true)}>Set an override</button>;
  }
  return (
    <form
      className="w-full space-y-2 rounded border border-neutral-200 p-3 dark:border-neutral-800"
      onSubmit={(e) => {
        e.preventDefault();
        set.mutate({ profileId, field, value, reason, ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}) });
        setOpen(false);
      }}
    >
      <p className="text-sm font-medium">Override a field temporarily</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-sm">Field
          <select className={inputClass} value={field} onChange={(e) => setField(e.target.value)}>
            {fields.map((f) => <option key={`${f.type}/${f.name}`} value={`${f.type}/${f.name}`}>{f.name}</option>)}
          </select>
        </label>
        <label className="text-sm">Play this instead
          <input className={inputClass} value={value} onChange={(e) => setValue(e.target.value)} required />
        </label>
        <label className="text-sm">Why
          <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} required />
        </label>
        <label className="text-sm">Until
          <input type="datetime-local" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} aria-describedby="override-expiry-hint" />
          <span id="override-expiry-hint" className="mt-1 block text-xs text-neutral-500">
            Leave empty for until further notice. An override with no expiry is shown as ageing until someone ends it.
          </span>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" className={btnPrimary}>Apply now</button>
        <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

function Versions({ environmentId, profileId }: { environmentId: string; profileId: string }) {
  const versions = useConfigVersions(environmentId, profileId);
  const rollback = useRollback(environmentId, profileId);
  if (!versions.data) return null;

  return (
    <Card title="History">
      <DataTable
        caption="Every published version of this profile in this environment, newest first"
        rows={versions.data.versions}
        rowKey={(v) => String(v.configVersion)}
        empty={<EmptyState title="Nothing published here yet" />}
        columns={[
          { key: "v", header: "Version", render: (v) => `v${v.configVersion}` },
          { key: "when", header: "Published", render: (v) => <RelativeTime iso={v.publishedAt} /> },
          { key: "who", header: "By", render: (v) => v.publishedBy },
          {
            key: "how", header: "Source",
            render: (v) => v.source === "promotion" ? `promoted from ${v.sourceEnvironmentId}` : v.source === "rollback" ? `rolled back to v${v.rolledBackFromVersion}` : "edited here",
          },
          {
            key: "action", header: "",
            render: (v) => (
              <button type="button" className="text-sm underline disabled:opacity-50" disabled={rollback.isPending} onClick={() => rollback.mutate({ version: v.configVersion })}>
                Roll back to this
              </button>
            ),
          },
        ]}
      />
      {rollback.isSuccess && <p role="status" className="mt-2 text-sm text-green-800 dark:text-green-300">Rolled back.</p>}
    </Card>
  );
}


/**
 * Section 10.2: a name that exists in every environment resolves on its
 * own, so this is only ever the exception - where the instance calls the
 * same thing something else, or where it was renamed underneath the
 * configuration. Offered exactly where the problem is reported.
 */
function AliasFixer({ environmentId, drift }: { environmentId: string; drift: { field: string; type: string; ref: string } }) {
  const inventory = useInventory(environmentId);
  const setAlias = useSetAlias(environmentId);
  const [resolvesTo, setResolvesTo] = useState("");
  const candidates = (inventory.data?.items ?? []).filter((i) => i.type === drift.type);

  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 border-t border-red-200 pt-2 dark:border-red-900"
      onSubmit={(e) => {
        e.preventDefault();
        if (resolvesTo) setAlias.mutate({ type: drift.type, name: drift.ref, resolvesTo });
      }}
    >
      <span className="text-sm">
        <code className="text-xs">{drift.ref}</code> ({drift.type}) is not in this instance.
      </span>
      <label className="text-xs">
        It is called
        <select
          className="mt-1 block rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          aria-label={`What ${drift.ref} is called here`}
          value={resolvesTo}
          onChange={(e) => setResolvesTo(e.target.value)}
        >
          <option value="">Choose a name</option>
          {candidates.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </label>
      <button type="submit" className={btnPrimary} disabled={!resolvesTo || setAlias.isPending}>
        Map it here
      </button>
    </form>
  );
}

/** Name it; everything else is authored on the profile afterwards. The API records who created it. */
function NewProfileForm({ environmentId, onCreated, onCancel }: { environmentId: string; onCreated: (profileId: string) => void; onCancel?: () => void }) {
  const create = useCreateProfile(environmentId);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <Card title="New profile">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate({ name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}) }, { onSuccess: (r) => onCreated(r.profileId) });
        }}
      >
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={128} placeholder="Global Helpdesk" className={`${inputClass} w-64`} />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Description (optional)</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={512} placeholder="One flow, every inbound number." className={`${inputClass} w-96`} />
        </label>
        <button type="submit" disabled={create.isPending || !name.trim()} className={btnPrimary}>
          {create.isPending ? "Creating…" : "Create profile"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className={btnSecondary}>
            Cancel
          </button>
        )}
        {create.isError && <ErrorState error={create.error} />}
      </form>
    </Card>
  );
}
